import { faUserEdit } from "@fortawesome/pro-regular-svg-icons/faUserEdit";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons/faQuestionCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Typography } from "@mui/material";
import { blue, grey } from '@mui/material/colors';
import * as api from "api/Core";
import { GlobalClassKeys } from "assets/jss/common-styles";
import { SpinnerIndicator } from "components/CallServer";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import DashDoughnutBox from "components/Dashboard/DashDoughnutBox";
import GenericList from "components/GenericList";
import { JoinComponents } from "components/JoinComponents";
import MuiBox from "components/MuiExtras/MuiBox";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import { TopicDashboardResultsSummaryColumn } from "models/ApiEnums";
import { TopicDashboardResultsModel, TopicShortViewModel } from "models/ApiModels";
import * as React from "react";
import { Bar } from "react-chartjs-2";
import { WithTranslation } from "react-i18next";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    topic: TopicShortViewModel;
}

interface IState {
    data: TopicDashboardResultsModel;
    dialog: any;
    lastError: any;
    loading: boolean;
}

type Props = IProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

export class TopicDashboardResults extends React.Component<Props, IState>{

    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            dialog: null,
            lastError: null,
            loading: true,
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Dashboards.Topic.Results." + r, f);
    }

    ///
    private loadData() {
        api.getTopicDashboardResults({
            topicId: this.props.topic.topicId,
            columnToSummary: TopicDashboardResultsSummaryColumn.Resume,
            range: null
        })
            .then((result) => {
                this.setState({ data: (result as TopicDashboardResultsModel), loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, lastError: error, loading: false });
            });
    }

    ///
    private handleShowUsers = () => {
        this.setState({
            dialog: <GenericList
                title={this.res("ShowUsersTitle")}
                showThumbnail={true}
                variant="drawer"
                onGetData={
                    (searchText: string, offset: number, take: number) => api.getTopicDashboardResults(
                        {
                            topicId: this.props.topic.topicId,
                            columnToSummary: TopicDashboardResultsSummaryColumn.TopicUsers,
                            searchText: searchText,
                            range: { offset, take }
                        }
                    )}
                onClose={() => this.setState({ dialog: null })}
            />
        });

    };

    ///
    private renderHeader() {
        const { loading } = this.state;
        return (
            <MuiContainer>
                <MuiBox variant="notice">
                    <Grid container justifyContent="space-between" alignItems="center" >
                        <Grid item>

                            <MuiColor variant="success">
                                <Grid container alignItems="center" spacing={2} >
                                    <Grid item>
                                        <FontAwesomeIcon icon={faQuestionCircle} size="4x" />
                                    </Grid>
                                    <Grid item className={GlobalClassKeys.textCenter}>
                                        {
                                            loading
                                                ? <SpinnerIndicator />
                                                : <Typography variant="h5">{this.state.data.totalAnswers}</Typography>
                                        }
                                        <JoinComponents components={this.res("TotalAnswersText").split('\n')} separator={<br />} />
                                    </Grid>
                                </Grid>
                            </MuiColor>

                        </Grid>
                        <Grid item>

                            <MuiColor variant="info">
                                <Grid container alignItems="center" spacing={2} >
                                    <Grid item>
                                        <FontAwesomeIcon icon={faUserEdit} size="4x" />
                                    </Grid>
                                    <Grid item className={GlobalClassKeys.textCenter}>
                                        <MuiClickable onClick={this.handleShowUsers}>
                                            {
                                                loading
                                                    ? <SpinnerIndicator />
                                                    : <Typography variant="h5">{this.state.data.totalUsersInTopic}</Typography>
                                            }
                                            <JoinComponents components={this.res("UsersInTopicText").split('\n')} separator={<br />} />
                                        </MuiClickable>
                                    </Grid>
                                </Grid>
                            </MuiColor>

                        </Grid>
                    </Grid>
                </MuiBox >
            </MuiContainer>
        );
    }

    ///
    private renderGauges() {
        const { loading } = this.state;
        return (
            <MuiContainer>
                <Grid container justifyContent="space-between" alignItems="center" >
                    <Grid item>
                        {
                            loading
                                ? <SpinnerIndicator />
                                : <DashDoughnutBox
                                    key="1"
                                    active={false}
                                    title={this.res("GuageCorrectText")}
                                    titlePosition="top"
                                    value={this.state.data.indexCorrect}
                                    color="success"
                                />
                        }
                    </Grid>

                    <Grid item>
                        {
                            loading
                                ? <SpinnerIndicator />
                                : <DashDoughnutBox
                                    key="2"
                                    active={false}
                                    title={this.res("GuageBellow50Text")}
                                    titlePosition="top"
                                    value={this.state.data.indexUserBellow50}
                                    color="error"
                                />
                        }
                    </Grid>

                    <Grid item>
                        {
                            loading
                                ? <SpinnerIndicator />
                                : <DashDoughnutBox
                                    key="3"
                                    active={false}
                                    title={this.res("GuageParticipationText")}
                                    titlePosition="top"
                                    value={this.state.data.indexParticipation}
                                    color="success"
                                />
                        }
                    </Grid>
                </Grid>
            </MuiContainer>
        );
    }

    ///
    private renderDistribution() {

        if (this.state.loading) {
            return <SpinnerIndicator />;
        }

        const { data: { levelNotInitiate, levelBeginner, levelIntermediate, levelAdvanced} } = this.state;
        const ds = [levelNotInitiate, levelBeginner, levelIntermediate, levelAdvanced];
        const total = ds.reduce((p, c) => p + c);

        return (
            <MuiContainer>

                <div className={GlobalClassKeys.textCenter}>
                    <Typography variant="h5"><MuiColor variant="mutedDark">{this.res("DistributionTitle")}</MuiColor></Typography>
                </div>

                <div style={{ height: 150 }} >
                    <Bar
                        data={
                            {
                                labels: ["Distribuição"],
                                datasets: [
                                    {
                                        label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange00"),
                                        backgroundColor: grey[300],
                                        data: [ds[0] / total * 100.0]
                                    },
                                    {
                                        label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange1"),
                                        backgroundColor: blue[100],
                                        data: [ds[1] / total * 100.0]
                                    },
                                    {
                                        label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange2"),
                                        backgroundColor: blue[300],
                                        data: [ds[2] / total * 100.0]
                                    },
                                    {
                                        label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange3"),
                                        backgroundColor: blue[500],
                                        data: [ds[3] / total * 100.0]
                                    }
                                ]
                            }
                        }
                        options={
                            {
                                indexAxis: 'y',
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        display: true,
                                        position: 'bottom',
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: function (item: any) {
                                                return `${item.chart.data.datasets[item.datasetIndex].label}: ${parseFloat(item.formattedValue).toFixed(1)}% (${ds[item.datasetIndex]})`;
                                            },
                                        }
                                    }
                                },

                                scales: {
                                    x: {
                                        display: true,
                                        stacked: true,
                                        grid: {
                                            display: true
                                        },
                                        title: {
                                            display: true,
                                        },
                                        min: 0,
                                        max: 100,
                                        ticks: {
                                            callback: function (value: number) {
                                                return value.toFixed(0) + '%'; // convert it to percentage
                                            }
                                        },
                                    },
                                    y: {
                                        display: false,
                                        stacked: true,
                                        title: {
                                            display: true,
                                        }
                                    }
                                },

                            }
                        }
                    />
                </div>
            </MuiContainer>
        );
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public componentDidUpdate(prevProps: Props) {
    }

    ///
    public render() {
        const { lastError, dialog } = this.state;

        return (
            <>
                {
                    lastError
                        ? <RequestErrorReport error={lastError} />
                        : (
                            <div>
                                {this.renderHeader()}
                                {this.renderGauges()}
                                {this.renderDistribution()}
                                {dialog}
                            </div>
                        )
                }
            </>
        );
    }
}